import { faFileLines, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AccountLocationPicker } from '../components/AccountLocationPicker';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { Paginator } from '../components/Paginator';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalStatementService from '../services/portalStatementService';
import { useLoginModalStore } from '../stores/loginModalStore';

const PAGE_SIZE = 20;

/**
 * Statement history (PD-14046) — the customer's saved statements across all
 * assigned clients, modeled on the invoices page. Click into a statement for
 * the on-screen rendering and PDF download.
 */
export function StatementsPage(): JSX.Element {
	const { t } = useTranslation();
	const { token, isAuthenticated, availableClients } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
	const clientIdParam = searchParams.get('clientId') ?? '';

	const { data, isLoading } = useQuery({
		queryKey: ['portal-statements', currentPage, clientIdParam],
		queryFn: () =>
			portalStatementService.getStatements(token ?? '', {
				page: currentPage,
				limit: PAGE_SIZE,
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
			}),
		enabled: token != null,
		staleTime: 30_000,
	});

	const statements = data?.data ?? [];
	const pagination = data?.pagination ?? null;

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateParams({ page: page > 1 ? String(page) : null });
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},
		[updateParams],
	);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Statements" backPath="/account" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
				{t('Statements')}
			</h1>

			{/* Client/location filter */}
			{availableClients.length > 1 && (
				<div className="mb-4">
					<AccountLocationPicker
						value={clientIdParam}
						onChange={(value) => {
							updateParams({
								clientId: value.length > 0 ? value : null,
								page: null,
							});
						}}
						clients={availableClients}
					/>
				</div>
			)}

			{isLoading ? (
				<div className="flex items-center justify-center py-16 text-brand-muted">
					<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
					{t('Loading...')}
				</div>
			) : statements.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 text-brand-muted">
					<FontAwesomeIcon icon={faFileLines} className="text-3xl mb-3" />
					{t('No statements yet')}
				</div>
			) : (
				<div className="flex flex-col gap-3">
					{statements.map((statement) => (
						<button
							key={statement.id}
							type="button"
							onClick={() => {
								void navigate(`/statements/${statement.id}`);
							}}
							className="w-full text-left bg-brand-surface border border-brand-border rounded-xl p-4 hover:border-portal-accent transition-colors min-h-touch"
						>
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<div className="font-semibold text-gray-900 dark:text-gray-100">
										{t(statement.statementNumber)}
									</div>
									{availableClients.length > 1 && (
										<div className="text-sm text-brand-muted truncate">
											{t(statement.clientName)}
										</div>
									)}
									<div className="text-sm text-brand-muted">
										{t('As of')}{' '}
										{formatStatementDate(
											statement.asOfDate as unknown as string,
										)}
									</div>
								</div>
								<div className="text-right shrink-0">
									<div className="font-bold text-gray-900 dark:text-gray-100">
										{formatCurrency(statement.totalAmountDue)}
									</div>
									<div className="text-xs uppercase tracking-wide text-brand-muted">
										{t('Amount Due')}
									</div>
								</div>
							</div>
						</button>
					))}
				</div>
			)}

			{pagination != null && pagination.totalPages > 1 && (
				<div className="mt-4">
					<Paginator
						currentPage={pagination.page}
						totalPages={pagination.totalPages}
						onPageChange={handlePageChange}
					/>
				</div>
			)}
		</div>
	);
}
